import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const lines = [
  { text: "The Blue Mountains,", italic: false },
  { text: "in Private Comfort.", italic: true },
];

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex items-end"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="/assets/van-vineyard.png"
          alt="LucasPrivateTransfers Kia Carnival in the Megalong Valley"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-48 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 1 }}
          className="text-xs tracking-[0.35em] uppercase text-[#d4af37] mb-8"
          data-testid="hero-overline"
        >
          Blue Mountains · Greater Sydney · Bookings 7 Days
        </motion.p>

        <h1 className="font-serif-lux text-[11vw] sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight font-light [text-shadow:0_2px_30px_rgba(0,0,0,0.6)]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.italic ? "italic text-[#d4af37]" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 1.1,
                  delay: 0.25 + i * 0.16,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          className="mt-8 max-w-xl text-base leading-relaxed tracking-wide text-[#c8c8c8]"
          data-testid="hero-subtitle"
        >
          Private transfers and bespoke tours from the Blue Mountains to
          Greater Sydney — airports, weddings, Jenolan Caves, the Megalong
          Valley and the quiet places in between.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-reserve-button"
            onClick={() => scrollTo("booking")}
            className="group bg-[#d4af37] text-[#050505] px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-[#f3c746] transition-colors duration-500 flex items-center gap-3"
          >
            Book Your Transfer
            <ArrowRight
              size={14}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </button>
          <button
            data-testid="hero-fleet-button"
            onClick={() => scrollTo("services")}
            className="border border-white/25 text-[#fcfcfc] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-500"
          >
            Explore Services
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-16 flex flex-wrap gap-x-10 gap-y-3 text-[10px] tracking-[0.3em] uppercase text-[#999]"
          data-testid="hero-assurances"
        >
          <span>7-Seat Kia Carnival</span>
          <span className="text-[#d4af37]/50">·</span>
          <span>Live Flight Tracking</span>
          <span className="text-[#d4af37]/50">·</span>
          <span>Local Driver, Local Knowledge</span>
        </motion.div>
      </div>
    </section>
  );
}
